import React, {useState} from 'react';
import Popper from "popper.js";
import CancelIcon from "@material-ui/icons/Cancel";
import {IoChevronForward} from "react-icons/all";
import {SelectOption} from "../../common";
import {gql, useQuery} from "@apollo/client";
import {useDebounce} from "common/hook";

export const CLIENT_REPORT_USER_QUERY = gql`
    query clientReportUsersQuery($first: Int, $after: String, $params: ClientReportUsersQueryParamsInput!) {
        clientReportUsersQuery(after: $after, first: $first, params: $params) {
            edges {
                node {
                    name
                    guid
                }
            }
        }
    }
`

export const CLIENT_REPORT_CLIENT_QUERY = gql`
    query clientReportClientsQuery($first: Int, $after: String, $params: ClientReportClientsQueryParamsInput!) {
        clientReportClientsQuery(after: $after, first: $first, params: $params) {
            edges {
                node {
                    name
                    guid
                }
            }
        }
    }
`
type AutocompleteByUserNameResponse = {
    edges: Array<AutocompleteNode>;
}

type AutocompleteNode = {
    node: Autocomplete
}

type Autocomplete = {
    name: string;
    guid: string;
}

type AutoCompleteProps = {
    includeCurrentUser: boolean;
    value: string;
    setValue: (value: string) => void;
    setOption: (opt: SelectOption) => void;

    setFocusInput: () => void;
    selectedOptions: SelectOption[];
    personLookupType: 'Client' | 'ProviderUser';
}

function AutoCompleteQuery({
                               value,
                               setValue,
                               setOption,
                               selectedOptions,
                               includeCurrentUser,
                               setFocusInput,
                               personLookupType
                           }: AutoCompleteProps) {
    const onSelectOption = (opt: SelectOption) => {
        setValue("");
        setOption(opt);
        setFocusInput();
    }

    let graphqlQuery = CLIENT_REPORT_CLIENT_QUERY;
    if (personLookupType === 'ProviderUser') {
        graphqlQuery = CLIENT_REPORT_USER_QUERY;
    }
    let queryParams = {
        name: value,
        filterGuids: selectedOptions.map((opt) => opt.value),
    }
    if (personLookupType === 'ProviderUser') {
        // @ts-ignore
        queryParams['includeCurrentUser'] = includeCurrentUser
    }

    const {
        loading,
        data,
        error
    } = useQuery<{
        clientReportUsersQuery: AutocompleteByUserNameResponse,
        clientReportClientsQuery: AutocompleteByUserNameResponse
    }, {}>(graphqlQuery, {
        variables: {
            first: 10,
            after: null,
            params: queryParams
        },
        fetchPolicy: 'cache-first',
        skip: (value.length < 2),
    });

    if (error) {
        return <div>{error.message}</div>;
    }

    if (loading) {
        return <div className='loading-text'>Loading</div>;
    }
    let response = data?.clientReportClientsQuery;
    if (personLookupType === 'ProviderUser') {
        response = data?.clientReportUsersQuery
    }

    if (response === undefined || response.edges.length === 0) {
        return <div>{`There is no user matched`}</div>;
    }

    return (
        <ul>
            {
                response.edges.map((edge) => {

                    return <li className='pl-2 py-1 hover:bg-gray-200' key={edge.node.guid}
                               onClick={() => onSelectOption({label: edge.node.name, value: edge.node.guid})}>
                        {edge.node.name}
                    </li>;
                })
            }

        </ul>
    );
}

type OptionSelectedProps = {
    option: SelectOption;
    isColumn?: boolean;
    remove: () => void;
    isFirst?: boolean;
}

function OptionSelected({option, remove, isColumn = false, isFirst = false}: OptionSelectedProps) {

    return (<div
        className={`flex justify-between w-25 px-1 py-1 bg-botticelli-100 text-xs text-white rounded ${isColumn ? isFirst ? "" : "mb-1" : "ml-1"}`}>
        <button className='bg-transparent outline-none focus:outline-none pr-2'
                onClick={remove}>
            <div
                className='bg-transparent text-white opacity-5 outline-none focus:outline-none flex items-center'>
                <CancelIcon fontSize={"small"}/>
            </div>
        </button>
        <div className="leading-none truncate mt-1 select-none">
            {option.label}
        </div>
    </div>);
}

type ListOptionSelectedProps = {
    options: SelectOption[];
    disabled?: boolean;
    removeSelected: (opt: SelectOption) => void;
    limit?: number
}

function ListOptionSelected({options, removeSelected, disabled = false, limit = 2}: ListOptionSelectedProps) {
    const [dropdownPopoverShow, setDropdownPopoverShow] = React.useState(false);
    const btnRef = React.useRef(null);
    const rootRef = React.useRef(null);
    const popoverDropdownRef = React.useRef(null);
    const openDropdownPopover = () => {
        // for sonar cloud (this constructor use to set position of the dropdown)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        new Popper(btnRef.current!, popoverDropdownRef.current!, {
            placement: "right"
        });
        setDropdownPopoverShow(true);
    };

    const handleClickOutside = (e: Event) => {
        if ((rootRef.current as any).contains(e.target)) {
            return;
        }
        // setFieldValue(value);
        setDropdownPopoverShow(false);
    };

    React.useEffect(() => {
        // handle click outside
        if (dropdownPopoverShow) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownPopoverShow]);

    return (
        <div ref={rootRef} className="absolute flex right-0 items-center mt-1 mr-1">
            {
                options.map(
                    (opt, idx) => {
                        if (idx >= limit) {
                            return <div key={opt.value}/>;
                        }
                        return <OptionSelected key={opt.value} option={opt} remove={() => removeSelected(opt)}/>
                    })
            }
            <div ref={btnRef}>
                {
                    options.length > limit &&
                    <IoChevronForward className={'cursor-pointer'} size={24} color="#8FB1B3"
                                      onClick={() => openDropdownPopover()}/>
                }
            </div>

            <div ref={popoverDropdownRef}
                 className={
                     (dropdownPopoverShow && options.length > limit ? "block " : "hidden ") +
                     "p-2 bg-white text-black text-base z-50 float-left list-none text-left rounded shadow-lg mt-1 cursor-pointer select-none"
                 }
            >
                {
                    options.map(
                        (opt, idx) => {
                            if (idx < limit) {
                                return <div key={opt.value}/>;
                            }
                            return <OptionSelected key={opt.value}
                                                   option={opt}
                                                   remove={() => {
                                                       if (disabled) {
                                                           return
                                                       }
                                                       removeSelected(opt)

                                                   }}
                                                   isFirst={idx === limit}
                                                   isColumn/>
                        })
                }
            </div>
        </div>)
}

type ProviderUserMultipleInputProps = {
    fieldValues: SelectOption[];
    setFieldValues: (values: SelectOption[]) => void;
    includeCurrentUser?: boolean;
    disabled?: boolean;
    maxSelectNum?: number;
    personLookupType: 'ProviderUser' | 'Client'
}

export function ProviderPersonLookupMultipleInput({
                                                      fieldValues,
                                                      setFieldValues,
                                                      includeCurrentUser = false,
                                                      disabled,
                                                      maxSelectNum,
                                                      personLookupType,
                                                  }: ProviderUserMultipleInputProps) {
    const [value, setValue] = useState("");
    const debounceValue = useDebounce(value, 600); // delay value when input to debounce autocomplete query
    const [dropdownPopoverShow, setDropdownPopoverShow] = React.useState(false);
    const btnDropdownRef = React.useRef(null);
    const popoverDropdownRef = React.useRef(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const nodeRef = React.useRef(null);

    const setNewSelectedOption = (newOpt: SelectOption) => {
        const isExist = fieldValues.filter((opt) => newOpt.value === opt.value).length > 0;
        if (isExist) return;
        let newOptions = [newOpt, ...fieldValues]
        if (maxSelectNum && newOptions.length > maxSelectNum) {
            newOptions = [newOpt, ...fieldValues.slice(0, -1)]
        }

        setFieldValues(newOptions);
    }
    const openDropdownPopover = () => {
        // for sonar cloud (this constructor use to set position of the dropdown)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        new Popper(btnDropdownRef.current!, popoverDropdownRef.current!, {
            placement: "bottom"
        });
        setDropdownPopoverShow(true);
    };
    const handleClickOutside = (e: Event) => {
        if ((nodeRef.current as any).contains(e.target)) {
            return;
        }
        setValue("");
        setDropdownPopoverShow(false);
    };

    const setFocusInput = () => {
        if (inputRef) {
            inputRef.current?.focus();
        }
    };

    React.useEffect(() => {
        // handle click outside
        if (dropdownPopoverShow) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownPopoverShow, value]);

    let inputName = 'provider-user-input';
    if (personLookupType === 'Client') {
        inputName = 'client-input';
    }

    return (
        <div ref={nodeRef} className="inline-block relative text-sm w-full">
            <div ref={btnDropdownRef} className="relative">
                <ListOptionSelected options={fieldValues}
                                    disabled={disabled}
                                    removeSelected={(opt) =>
                                        setFieldValues(fieldValues.filter((selectedOpt) => opt.value !== selectedOpt.value))}/>
                <div className="w-full">
                    <input
                        name={inputName}
                        className={`block w-full bg-white border border-gray-400 hover:border-atlantis-100 ` +
                            `px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline ${disabled ? 'bg-gray-100 opacity-50 pointer-events-none' : ''}`}
                        onClick={openDropdownPopover}
                        value={value}
                        disabled={disabled}
                        onChange={(e) => setValue(e.target.value)}
                        autoComplete='off'
                        ref={inputRef}
                    />
                </div>

            </div>
            <div ref={popoverDropdownRef}
                 className={(dropdownPopoverShow ? "block " : "hidden ") + "w-full rounded shadow-lg z-50"}
            >
                {
                    value.length >= 2 &&
                    <div
                        className={`p-5 bg-white text-black text-base float-left list-none text-left mt-1 w-full rounded cursor-pointer select-none` +
                            ` ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        <AutoCompleteQuery value={debounceValue}
                                           setValue={setValue}
                                           includeCurrentUser={includeCurrentUser}
                                           setOption={setNewSelectedOption}
                                           selectedOptions={fieldValues}
                                           personLookupType={personLookupType}
                                           setFocusInput={setFocusInput}/>
                    </div>
                }
            </div>

        </div>
    );
}